import { useCallback, useEffect, useState } from "react";
import { getSessionSync } from "@/lib/session";
import {
  addSavedItem,
  removeSavedItem,
  type SavedKind,
  type SavedItem,
} from "@/lib/saved-items.functions";

const STORAGE_KEY = "flaneriet-bookmarks";
const EVENT = "flaneriet-bookmarks-changed";

/**
 * Interne nøgler i localStorage: `"kind:uuid"`.
 * Bagudkompatibel med den gamle model, hvor rene UUID-strenge betød artikel.
 */
function encodeKey(kind: SavedKind, id: string) {
  return `${kind}:${id}`;
}

function parseKey(key: string): SavedItem | null {
  // Ny form: "kind:uuid"
  const idx = key.indexOf(":");
  if (idx > 0) {
    const kind = key.slice(0, idx) as SavedKind;
    const id = key.slice(idx + 1);
    if (kind === "article" || kind === "recipe" || kind === "video") {
      return { kind, id };
    }
    return null;
  }
  // Gammel form: bare uuid – behandles som artikel
  if (/^[0-9a-f-]{36}$/i.test(key)) return { kind: "article", id: key };
  return null;
}

function readKeys(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as string[];
    // Migrér gamle rå UUIDer til "article:uuid" ved læsning
    const migrated = new Set<string>();
    for (const k of parsed) {
      const item = parseKey(k);
      if (item) migrated.add(encodeKey(item.kind, item.id));
    }
    return migrated;
  } catch {
    return new Set();
  }
}

/** Skriv lokalt + emit til alle useBookmarks-abonnenter. */
export function saveKeys(keys: Set<string>) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...keys]));
  window.dispatchEvent(new Event(EVENT));
}

/** Bagudkompatibel eksport (bruges af cloud-sync). */
export function saveItems(items: SavedItem[]) {
  const keys = new Set(items.map((it) => encodeKey(it.kind, it.id)));
  saveKeys(keys);
}

export function useBookmarks() {
  const [keys, setKeys] = useState<Set<string>>(() => readKeys());

  useEffect(() => {
    const sync = () => setKeys(readKeys());
    sync();
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggle = useCallback((kind: SavedKind, id: string) => {
    const key = encodeKey(kind, id);
    const next = new Set(readKeys());
    const willAdd = !next.has(key);
    if (willAdd) next.add(key);
    else next.delete(key);
    saveKeys(next);
    setKeys(next);
    if (getSessionSync()) {
      (willAdd
        ? addSavedItem({ data: { kind, id } })
        : removeSavedItem({ data: { kind, id } })
      ).catch(() => {
        // stille – lokalt er kilden indtil næste sync
      });
    }
  }, []);

  const isBookmarked = useCallback(
    (kind: SavedKind, id: string) => keys.has(encodeKey(kind, id)),
    [keys],
  );

  /** Alle gemte id'er af én bestemt slags. */
  const idsOfKind = useCallback(
    (kind: SavedKind): Set<string> => {
      const out = new Set<string>();
      const prefix = `${kind}:`;
      for (const k of keys) {
        if (k.startsWith(prefix)) out.add(k.slice(prefix.length));
      }
      return out;
    },
    [keys],
  );

  return {
    keys,
    toggle,
    isBookmarked,
    idsOfKind,
    count: keys.size,
  };
}
